import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { NumeroDocumentField } from './NumeroDocumentField';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const UNITES = ['jour', 'heure', 'heure stagiaire', 'forfait', 'candidat', 'unité'];

export const FactureEditDialog = ({ doc, open, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [numeroValide, setNumeroValide] = useState(true);

  useEffect(() => {
    if (doc && open) {
      setNumeroValide(true);
      setForm({
        numero: doc.numero || '',
        destinataire_nom: doc.destinataire_nom || '', destinataire_adresse: doc.destinataire_adresse || '', destinataire_contact: doc.destinataire_contact || '',
        code_action_client: doc.code_action_client || '', references_bdc: (doc.references_bdc || []).join(', '),
        date_emission: doc.date_emission || '', date_echeance: doc.date_echeance || '', mode_reglement: doc.mode_reglement || '', notes: doc.notes || '',
        taux_tva: doc.taux_tva ?? 20,
        lignes: (doc.lignes || []).length ? doc.lignes.map(l => ({ ...l })) : [{ description: '', quantite: 1, unite: 'jour', prix_unitaire_ht: doc.montant_ht || 0 }],
      });
    }
  }, [doc, open]);

  if (!form || !doc) return null;
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const setLigne = (i, patch) => set({ lignes: form.lignes.map((l, j) => j === i ? { ...l, ...patch } : l) });
  const totalHt = form.lignes.reduce((t, l) => t + (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0), 0);
  const tva = totalHt * (Number(form.taux_tva) || 0) / 100;

  const save = async () => {
    if (!numeroValide) { toast.error('Ce numéro est déjà utilisé par un autre document'); return; }
    if (!(form.numero || '').trim()) { toast.error('Le numéro est obligatoire'); return; }
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/factures/${doc.id}/contenu`, {
        ...form,
        numero: form.numero.trim(),
        references_bdc: form.references_bdc.split(',').map(s => s.trim()).filter(Boolean),
        code_action_client: form.code_action_client || null,
        taux_tva: Number(form.taux_tva) || 0,
        lignes: form.lignes.map(l => ({ ...l, quantite: Number(l.quantite) || 0, prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0 })),
      });
      toast.success(`${res.data?.numero || doc.numero} mise à jour`);
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="facture-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier {doc.type_document === 'avoir' ? 'l\'avoir' : 'la facture'} <span className="font-mono text-indigo-600">{doc.numero}</span></DialogTitle>
          <DialogDescription>Le dossier n'est pas modifiable. Le numéro peut être changé s'il n'existe pas déjà. Les montants sont recalculés à partir des lignes.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <NumeroDocumentField value={form.numero} onChange={(v) => set({ numero: v })} typeDocument={doc.type_document} excludeId={doc.id} onValidite={setNumeroValide} />
          <div className="space-y-1"><Label className="text-xs">Destinataire</Label><Input value={form.destinataire_nom} onChange={(e) => set({ destinataire_nom: e.target.value })} data-testid="facture-edit-destinataire" /></div>
          <div className="space-y-1"><Label className="text-xs">Contact</Label><Input value={form.destinataire_contact} onChange={(e) => set({ destinataire_contact: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Adresse</Label><Input value={form.destinataire_adresse} onChange={(e) => set({ destinataire_adresse: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Code action client</Label><Input value={form.code_action_client} onChange={(e) => set({ code_action_client: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Réf. bons de commande (séparées par des virgules)</Label><Input value={form.references_bdc} onChange={(e) => set({ references_bdc: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Date d'émission</Label><Input type="date" value={form.date_emission} onChange={(e) => set({ date_emission: e.target.value })} data-testid="facture-edit-date" /></div>
          <div className="space-y-1"><Label className="text-xs">Date d'échéance</Label><Input type="date" value={form.date_echeance} onChange={(e) => set({ date_echeance: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Mode de règlement</Label><Input value={form.mode_reglement} onChange={(e) => set({ mode_reglement: e.target.value })} placeholder="virement, chèque…" /></div>
          <div className="space-y-1"><Label className="text-xs">TVA (%)</Label><Input type="number" step="0.1" min="0" value={form.taux_tva} onChange={(e) => set({ taux_tva: e.target.value })} data-testid="facture-edit-tva" /></div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Lignes</Label>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => set({ lignes: [...form.lignes, { description: '', quantite: 1, unite: 'jour', prix_unitaire_ht: 0 }] })} data-testid="facture-edit-add-ligne"><Plus className="w-3 h-3 mr-1" /> Ligne</Button>
          </div>
          <div className="grid grid-cols-[1fr_80px_110px_110px_100px_32px] gap-2 text-[10px] uppercase text-slate-400 px-1"><span>Désignation</span><span>Qté</span><span>Unité</span><span>PU HT</span><span className="text-right">Montant</span><span /></div>
          {form.lignes.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_110px_110px_100px_32px] gap-2 items-center" data-testid={`facture-edit-ligne-${i}`}>
              <Input value={l.description} onChange={(e) => setLigne(i, { description: e.target.value })} className="h-8 text-xs" data-testid={`facture-edit-desc-${i}`} />
              <Input type="number" step="0.5" min="0" value={l.quantite} onChange={(e) => setLigne(i, { quantite: e.target.value })} className="h-8 text-xs" data-testid={`facture-edit-qte-${i}`} />
              <select value={l.unite || 'jour'} onChange={(e) => setLigne(i, { unite: e.target.value })} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
                {UNITES.includes(l.unite) || !l.unite ? null : <option value={l.unite}>{l.unite}</option>}
                {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <Input type="number" step="0.01" min="0" value={l.prix_unitaire_ht} onChange={(e) => setLigne(i, { prix_unitaire_ht: e.target.value })} className="h-8 text-xs" data-testid={`facture-edit-pu-${i}`} />
              <span className="text-xs font-semibold text-right">{eur((Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0))}</span>
              <button type="button" onClick={() => set({ lignes: form.lignes.filter((_, j) => j !== i) })} disabled={form.lignes.length === 1} className="text-slate-300 hover:text-red-500 disabled:opacity-30" title="Supprimer la ligne"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex justify-end gap-6 text-xs pt-2 border-t">
            <span>HT : <b data-testid="facture-edit-total-ht">{eur(totalHt)}</b></span>
            <span>TVA : <b>{eur(tva)}</b></span>
            <span className="text-sm">TTC : <b className="text-indigo-700" data-testid="facture-edit-total-ttc">{eur(totalHt + tva)}</b></span>
          </div>
        </div>

        <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} onClick={save} data-testid="facture-edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Enregistrer</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
